// Catalog.ID Client — Organization Claims Pane
// ============================================================================
// Lists and manages claims belonging to the organization.
// The delegate unwraps the org's attr_encrypt key from their wrapped copy,
// then uses it to decrypt/encrypt org claims.

import { createListPaneType, createElement, renderPaneActions } from '@catalog-shell';
import type { ListPaneConfig, PaneActionDescriptor, PaneTypeRenderContext } from '@catalog-shell';
import { orgWorkspaceActions } from './org-workspace-actions';
import { DataSource } from '@catalog-shell/components';
import type { ColumnDef, FetchResult } from '@catalog-shell/components';
import { claimTypeLabel } from './claims-list-pane';
import type { ValidationStatus } from '../components/certificate-badge';
import * as organizationApi from '../api/organization';
import { getOrgAttrKey } from '../state/org-keys';
import { identity } from '../state/identity';
import type { ClaimSummary } from '../api/types';
import { PANE_IDS } from '../shell-config';


const STATUS_COLORS: Record<ValidationStatus, string> = {
    unvalidated: '#555',
    pending: 'rgba(100,149,237,0.3)',
    validated: 'rgba(80,200,80,0.3)',
    expired: 'rgba(220,150,50,0.3)',
    revoked: 'rgba(220,50,50,0.3)',
};

const STATUS_LABELS: Record<ValidationStatus, string> = {
    unvalidated: 'Unvalidated',
    pending: 'Pending',
    validated: 'Validated',
    expired: 'Expired',
    revoked: 'Revoked',
};

const columns: ColumnDef<ClaimSummary>[] = [
    {
        key: 'claimType',
        header: 'Type',
        width: 120,
        sortable: true,
        getValue: (item) => item.claimType,
        type: { kind: 'text', converter: (v) => claimTypeLabel(v as string) },
    },
    {
        key: 'encryptedLabel',
        header: 'Label',
        width: 180,
        getValue: (item) => item.encryptedLabel,
        type: { kind: 'text', converter: (v) => v ? '(encrypted)' : '—', textColor: () => '#555' },
    },
    {
        key: 'validationStatus',
        header: 'Status',
        width: 110,
        getValue: (item) => item.revokedAt ? 'revoked' : (item.validationStatus || 'unvalidated'),
        type: {
            kind: 'badge',
            backgroundColor: (v) => STATUS_COLORS[(v as ValidationStatus)] ?? '#555',
            converter: (v) => STATUS_LABELS[(v as ValidationStatus)] ?? String(v),
        },
    },
    {
        key: 'creationTime',
        header: 'Created',
        width: 100,
        sortable: true,
        getValue: (item) => item.creationTime,
        type: {
            kind: 'text',
            converter: (v) => {
                try { return new Date(v as string).toLocaleDateString(); }
                catch { return String(v); }
            },
        },
        responsiveHide: { minContainerWidth: 600, priority: 1 },
    },
];

function createOrgClaimsDataSource(): DataSource<ClaimSummary> {
    return new DataSource<ClaimSummary>({
        fetch: async (params): Promise<FetchResult<ClaimSummary>> => {
            const orgCid = identity.getOrganizationCid();
            if (!orgCid) return { items: [], totalCount: 0 };
            const claims = await organizationApi.listOrgClaims(orgCid);
            const sliced = claims.slice(params.offset, params.offset + params.limit);
            return { items: sliced, totalCount: claims.length };
        },
        keyFn: (item) => item.claimUuid,
        pageSize: 100,
    });
}

/**
 * The org-claims pane's title-row verbs: the pane's own "+ New Org Claim"
 * first, then the organization's workspace verbs (org-workspace-actions.ts),
 * which all fold into the trailing ⋯ menu.
 *
 * Creating a claim for the ORGANISATION is owner/admin work — the same gate the
 * server applies to `POST /api/organization/{cid}/claims` — and, like the verbs
 * beside it, it is OMITTED rather than disabled for a role that cannot use it.
 */
export function orgClaimsActions(ctx: PaneTypeRenderContext): PaneActionDescriptor[] {
    const actions: PaneActionDescriptor[] = [];

    if (identity.hasRole('owner') || identity.hasRole('admin')) {
        actions.push({
            key: 'org.new-claim',
            label: '+ New Org Claim',
            variant: 'primary',
            execute: () => {
                // `isOrgClaim` is what makes the shared claim form encrypt to
                // the ORG's attr_encrypt key and POST to the org's route.
                ctx.surface?.openPane({
                    typeId: PANE_IDS.claimDetail,
                    title: 'New Org Claim',
                    context: { mode: 'new', isOrgClaim: true },
                }, { originPaneId: ctx.instance.instanceId });
            },
        });
    }

    actions.push(...orgWorkspaceActions(ctx.shell));
    return actions;
}

const config: ListPaneConfig<ClaimSummary> = {
    typeId: PANE_IDS.orgClaims,
    defaultTitle: 'Organization Claims',
    createDataSource: () => createOrgClaimsDataSource(),
    columns,
    keyFn: (item) => item.claimUuid,
    initialMode: 'table',
    enabledModes: ['table'],
    selectionMode: 'none',
    onCreated: ({ dataSource }, ctx) => {
        ctx.disposables.add(ctx.shell.on('data-changed', (data) => {
            if ((data as { type?: string })?.type === 'claims') {
                dataSource.refresh();
            }
        }));
    },
    onItemSelect: (item, ctx) => {
        ctx.surface?.openPane({
            typeId: PANE_IDS.claimDetail,
            title: claimTypeLabel(item.claimType),
            context: { claimUuid: item.claimUuid, isOrgClaim: true },
        }, { originPaneId: ctx.instance.instanceId });
    },
    // The organization's own verbs (rotate keys, revalidate domain, flatten the
    // key chain, terminate) ride this pane's title row — see
    // org-workspace-actions.ts. They are role-gated by omission, and `identity`
    // only changes when the delegate's roles do, which re-renders the tab.
    tabBarActions: (ctx) => renderPaneActions(
        orgClaimsActions(ctx), { overflowTitle: 'Organization actions' }),
    // Readiness banner above the list: without the org attr key the rows stay
    // encrypted, and that is a property of the pane's content, not a verb.
    createHeader: () => {
        const banner = createElement('div', { className: 'cid-pane-error' });
        void (async () => {
            const orgKey = await getOrgAttrKey();
            if (orgKey) return;
            banner.textContent = 'Org key not available';
            banner.classList.add('cid-pane-error--visible');
        })();
        return banner;
    },
    ariaLabel: 'Organization claims',
};

export function createOrgClaimsPane() {
    return createListPaneType(config);
}
